import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Copy, Trash2, TrendingUp, TrendingDown, DollarSign, CheckCircle2, ArrowRight, ChevronDown, Coins, Clock, BarChart3, Fuel } from 'lucide-react';
import CryptoAffiliateBlock from '../../CryptoAffiliateBlock';

const EthereumRoiCalculatorTool = () => {
  const [initialInvestment, setInitialInvestment] = useState('');
  const [currentValue, setCurrentValue] = useState('');
  const [duration, setDuration] = useState('');
  const [durationUnit, setDurationUnit] = useState('months');
  const [stakingRewards, setStakingRewards] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');
  const [showCoinMenu, setShowCoinMenu] = useState(false);

  useEffect(() => {
    calculate();
  }, [initialInvestment, currentValue, duration, durationUnit, stakingRewards]);

  const calculate = () => {
    const initial = parseFloat(initialInvestment);
    const current = parseFloat(currentValue);
    const dur = parseFloat(duration) || 0;
    const staking = parseFloat(stakingRewards) || 0;

    if (isNaN(initial) || isNaN(current) || initial <= 0) {
      setResult(null);
      return;
    }

    const totalValue = current + staking;
    const profit = totalValue - initial;
    const roi = ((totalValue - initial) / initial) * 100;
    const growthMultiplier = totalValue / initial;
    const priceOnlyRoi = ((current - initial) / initial) * 100;

    let durationInYears = dur;
    if (durationUnit === 'days') {
      durationInYears = dur / 365;
    } else if (durationUnit === 'months') {
      durationInYears = dur / 12;
    }

    let annualizedRoi = 0;
    if (durationInYears > 0 && initial > 0 && totalValue > 0) {
      annualizedRoi = (Math.pow(totalValue / initial, 1 / durationInYears) - 1) * 100;
    }

    const dailyReturn = durationInYears > 0 ? roi / (durationInYears * 365) : 0;
    const monthlyReturn = durationInYears > 0 ? roi / (durationInYears * 12) : 0;
    const stakingYield = staking > 0 && durationInYears > 0 ? (staking / initial) / durationInYears * 100 : 0;

    setResult({
      profit,
      roi,
      priceOnlyRoi,
      annualizedRoi,
      growthMultiplier,
      dailyReturn,
      monthlyReturn,
      durationInYears,
      stakingYield,
      totalValue,
      stakingRewards: staking,
      isProfitable: profit > 0,
      isBreakEven: Math.abs(profit) < 0.01
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(typeof value === 'number' ? value.toFixed(2) : value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setInitialInvestment('');
    setCurrentValue('');
    setDuration('');
    setStakingRewards('');
    setResult(null);
  };

  const formatCurrency = (value) => '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatPercent = (value) => (value >= 0 ? '+' : '') + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

  const scenarios = [
    { label: '$5K → $12K (1yr)', initial: '5000', current: '12000', dur: '12', unit: 'months', staking: '200' },
    { label: '$10K → $35K (2yr)', initial: '10000', current: '35000', dur: '2', unit: 'years', staking: '800' },
    { label: '$2K → $8K (6mo)', initial: '2000', current: '8000', dur: '6', unit: 'months', staking: '50' },
  ];

  const applyScenario = (scenario) => {
    setInitialInvestment(scenario.initial);
    setCurrentValue(scenario.current);
    setDuration(scenario.dur);
    setDurationUnit(scenario.unit);
    setStakingRewards(scenario.staking);
  };

  const coinOptions = [
    { name: 'Bitcoin (BTC)', slug: 'bitcoin-roi-calculator', icon: '₿', current: false },
    { name: 'Ethereum (ETH)', slug: 'ethereum-roi-calculator', icon: 'Ξ', current: true },
    { name: 'All Cryptocurrencies', slug: 'crypto-roi-calculator', icon: '🪙', current: false },
  ];

  const getRoiRating = (roi) => {
    if (roi >= 100) return { label: 'Exceptional', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (roi >= 50) return { label: 'Excellent', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (roi >= 20) return { label: 'Good', color: 'text-blue-400', bg: 'bg-blue-500/20' };
    if (roi >= 0) return { label: 'Positive', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (roi >= -20) return { label: 'Minor Loss', color: 'text-orange-400', bg: 'bg-orange-500/20' };
    return { label: 'Significant Loss', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  return (
    <div className="space-y-8" data-testid="ethereum-roi-calculator">
      {/* Coin Selector Dropdown */}
      <div className="relative">
        <button
          onClick={() => setShowCoinMenu(!showCoinMenu)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 border border-indigo-500/30 rounded-lg text-indigo-400 hover:bg-indigo-500/30 transition-colors"
          data-testid="coin-selector"
        >
          <span className="text-xl">Ξ</span>
          <span className="font-medium">Ethereum ROI Calculator</span>
          <ChevronDown size={16} className={`transition-transform ${showCoinMenu ? 'rotate-180' : ''}`} />
        </button>
        
        {showCoinMenu && (
          <div className="absolute top-full left-0 mt-2 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
            {coinOptions.map((coin) => (
              coin.current ? (
                <div key={coin.slug} className="flex items-center gap-3 px-4 py-3 bg-indigo-500/10 text-indigo-400 cursor-default">
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <CheckCircle2 size={16} className="ml-auto" />
                </div>
              ) : (
                <Link
                  key={coin.slug}
                  to={`/tools/${coin.slug}`}
                  className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 transition-colors"
                  onClick={() => setShowCoinMenu(false)}
                >
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <ArrowRight size={16} className="ml-auto opacity-50" />
                </Link>
              )
            ))}
          </div>
        )}
      </div>

      {/* Quick Examples */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-400 mr-2">Quick Examples:</span>
        {scenarios.map((scenario, index) => (
          <button
            key={index}
            onClick={() => applyScenario(scenario)}
            className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-full text-gray-300 transition-colors"
            data-testid={`scenario-${index}`}
          >
            {scenario.label}
          </button>
        ))}
      </div>

      {/* Calculator Input Section */}
      <div className="p-6 bg-gray-800 rounded-xl space-y-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
            <span className="text-2xl text-indigo-400">Ξ</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Ethereum ROI Calculator</h3>
            <p className="text-sm text-gray-400">Calculate ETH returns including staking rewards</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Initial Investment (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={initialInvestment}
                onChange={(e) => setInitialInvestment(e.target.value)}
                placeholder="e.g., 5000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-indigo-500 focus:outline-none text-lg"
                data-testid="initial-investment-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Amount originally invested in ETH</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Current Value (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={currentValue}
                onChange={(e) => setCurrentValue(e.target.value)}
                placeholder="e.g., 12000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-indigo-500 focus:outline-none text-lg"
                data-testid="current-value-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Current value of your ETH holdings</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Investment Duration
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                step="1"
                min="0"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="e.g., 12"
                className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-indigo-500 focus:outline-none"
                data-testid="duration-input"
              />
              <select
                value={durationUnit}
                onChange={(e) => setDurationUnit(e.target.value)}
                className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-indigo-500 focus:outline-none"
              >
                <option value="days">Days</option>
                <option value="months">Months</option>
                <option value="years">Years</option>
              </select>
            </div>
            <p className="text-xs text-gray-500 mt-1">Required for annualized ROI</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Staking Rewards (USD) <span className="text-gray-500">(optional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={stakingRewards}
                onChange={(e) => setStakingRewards(e.target.value)}
                placeholder="e.g., 200"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-indigo-500 focus:outline-none"
                data-testid="staking-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">ETH earned from staking (in USD)</p>
          </div>
        </div>

        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors text-sm"
          data-testid="clear-btn"
        >
          <Trash2 size={14} />
          Clear All
        </button>
      </div>

      {/* Results Section */}
      {result && (
        <div className="space-y-4">
          {/* Main Result - ROI */}
          <div className={`p-6 rounded-xl border-2 ${result.isProfitable ? 'bg-green-500/10 border-green-500/40' : result.isBreakEven ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-red-500/10 border-red-500/40'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {result.isProfitable ? (
                  <TrendingUp className="text-green-400" size={28} />
                ) : result.isBreakEven ? (
                  <DollarSign className="text-yellow-400" size={28} />
                ) : (
                  <TrendingDown className="text-red-400" size={28} />
                )}
                <span className="text-lg font-medium text-gray-200">Total Ethereum ROI</span>
              </div>
              <button
                onClick={() => copyResult(result.roi, 'roi')}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                {copied === 'roi' ? <CheckCircle2 size={18} className="text-green-400" /> : <Copy size={18} className="text-gray-400" />}
              </button>
            </div>
            <div className="text-center">
              <p className={`text-5xl font-bold ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {formatPercent(result.roi)}
              </p>
              <div className="mt-2">
                <span className={`inline-block px-3 py-1 rounded-full text-sm ${getRoiRating(result.roi).bg} ${getRoiRating(result.roi).color}`}>
                  {getRoiRating(result.roi).label}
                </span>
              </div>
              <p className="text-gray-400 mt-2">
                Your Ethereum grew <span className="text-white font-semibold">{result.growthMultiplier.toFixed(2)}x</span>
                {result.stakingRewards > 0 && <span className="text-indigo-400"> (including staking)</span>}
              </p>
            </div>
          </div>

          {/* Detailed Results Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Total Profit</p>
              <p className={`text-xl font-semibold ${result.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {result.profit >= 0 ? '+' : ''}{formatCurrency(result.profit)}
              </p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Annualized ROI</p>
              <p className={`text-xl font-semibold ${result.annualizedRoi >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                {result.durationInYears > 0 ? formatPercent(result.annualizedRoi) : 'N/A'}
              </p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Price ROI Only</p>
              <p className={`text-xl font-semibold ${result.priceOnlyRoi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatPercent(result.priceOnlyRoi)}
              </p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Total Value</p>
              <p className="text-xl font-semibold text-white">{formatCurrency(result.totalValue)}</p>
            </div>
          </div>

          {/* Staking Info */}
          {result.stakingRewards > 0 && (
            <div className="p-4 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 size={18} className="text-indigo-400" />
                <h4 className="text-sm font-medium text-gray-200">Staking Contribution</h4>
              </div>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-400">Staking Rewards</p>
                  <p className="text-indigo-400 font-semibold">{formatCurrency(result.stakingRewards)}</p>
                </div>
                <div>
                  <p className="text-gray-400">Effective Staking Yield</p>
                  <p className="text-indigo-400 font-semibold">{result.stakingYield.toFixed(2)}% APY</p>
                </div>
                <div>
                  <p className="text-gray-400">% of Total Gains</p>
                  <p className="text-indigo-400 font-semibold">
                    {result.profit > 0 ? ((result.stakingRewards / result.profit) * 100).toFixed(1) : 0}%
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Period Returns */}
          {result.durationInYears > 0 && (
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                <Clock size={16} className="text-indigo-400" />
                Average Returns by Period
              </h4>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-gray-400 text-xs mb-1">Daily</p>
                  <p className={`font-semibold ${result.dailyReturn >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {formatPercent(result.dailyReturn)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Monthly</p>
                  <p className={`font-semibold ${result.monthlyReturn >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {formatPercent(result.monthlyReturn)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Yearly (CAGR)</p>
                  <p className={`font-semibold ${result.annualizedRoi >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                    {formatPercent(result.annualizedRoi)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Gas Fee Note */}
          <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/20 flex items-start gap-3">
            <Fuel size={18} className="text-orange-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-400">
              <span className="text-orange-400 font-medium">Note:</span> This calculation doesn't include Ethereum gas fees paid during transactions. Gas costs can impact ROI, especially for smaller investments or frequent trading.
            </p>
          </div>
          
          {/* Affiliate CTA Block */}
          <CryptoAffiliateBlock 
            profit={result.profit} 
            cryptoName="ETH"
            toolName="Ethereum ROI Calculator"
            toolSlug="ethereum-roi-calculator"
          />
        </div>
      )}

      {/* Related Crypto Tools - Internal Links */}
      <div className="p-5 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-xl border border-indigo-500/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Coins size={20} className="text-indigo-400" />
          Related Ethereum & Crypto Tools
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link
            to="/tools/ethereum-profit-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-indigo-400 transition-colors">ETH Profit</p>
              <p className="text-sm text-gray-400">Calculate profit</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-indigo-400 transition-colors" />
          </Link>
          <Link
            to="/tools/bitcoin-roi-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-orange-400 transition-colors">BTC ROI</p>
              <p className="text-sm text-gray-400">Bitcoin returns</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-orange-400 transition-colors" />
          </Link>
          <Link
            to="/tools/crypto-gain-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-green-400 transition-colors">Crypto Gains</p>
              <p className="text-sm text-gray-400">Track gains</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-green-400 transition-colors" />
          </Link>
          <Link
            to="/tools/crypto-tax-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-purple-400 transition-colors">Crypto Tax</p>
              <p className="text-sm text-gray-400">Estimate taxes</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-purple-400 transition-colors" />
          </Link>
        </div>
      </div>

      {/* Formula Reference */}
      <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
        <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
          <Calculator size={16} className="text-purple-400" />
          Ethereum ROI Formulas
        </h4>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="bg-gray-900 p-3 rounded-lg">
            <p className="text-gray-400 mb-1">Total ROI (with staking):</p>
            <code className="text-purple-400">((Current + Staking - Initial) / Initial) × 100</code>
          </div>
          <div className="bg-gray-900 p-3 rounded-lg">
            <p className="text-gray-400 mb-1">Annualized ROI (CAGR):</p>
            <code className="text-purple-400">(TotalValue/Initial)^(1/years) - 1</code>
          </div>
        </div>
      </div>

      {/* Tips Section */}
      <div className="p-5 bg-gradient-to-r from-green-500/10 to-teal-500/10 rounded-xl border border-green-500/20">
        <h4 className="text-lg font-semibold text-white mb-3">💡 Ethereum ROI Insights</h4>
        <ul className="space-y-2 text-gray-300">
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Staking Matters:</strong> Since The Merge, ETH staking yields 3-5% APY, significantly boosting long-term ROI</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>ETH/BTC Ratio:</strong> Track Ethereum's performance relative to Bitcoin for market cycle insights</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Network Effects:</strong> ETH ROI often correlates with DeFi and NFT market activity</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Gas Optimization:</strong> Use Layer 2 solutions to reduce transaction costs and preserve ROI</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>EIP Tracking:</strong> Monitor Ethereum improvement proposals that may affect price and utility</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default EthereumRoiCalculatorTool;
